import { memo, useCallback, useEffect, useState } from 'react';

import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import { Fab, Tooltip, useTheme, Zoom } from '@mui/material';

interface ScrollToTopButtonProps {
    enabled?: boolean;
    threshold?: number;
    bottom?: number;
    right?: number;
    'data-locator'?: string;
}

function ScrollToTopButton({
    enabled = true,
    threshold = 400,
    bottom = 100,
    right = 24,
    'data-locator': dataLocator,
}: ScrollToTopButtonProps) {
    const theme = useTheme();
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        if (!enabled) {
            setIsVisible(false);
            return;
        }

        const handleScroll = () => {
            const shouldShow = window.scrollY > threshold;
            setIsVisible(shouldShow);
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        handleScroll();

        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [enabled, threshold]);

    const handleClick = useCallback(() => {
        window.scrollTo({
            top: 0,
            behavior: 'smooth',
        });
    }, []);

    if (!enabled || !isVisible) {
        return null;
    }

    return (
        <Zoom in={isVisible}>
            <Tooltip title="Scroll to top" placement="left">
                <Fab
                    color="primary"
                    size="medium"
                    aria-label="scroll to top"
                    onClick={handleClick}
                    data-locator={dataLocator}
                    sx={{
                        position: 'fixed',
                        right,
                        bottom,
                        zIndex: theme.zIndex.appBar + 1,
                        boxShadow: theme.shadows[8],
                        '&:hover': {
                            boxShadow: theme.shadows[12],
                            transform: 'translateY(-2px)',
                        },
                        transition: 'all 0.3s ease-in-out',
                    }}
                >
                    <ArrowUpwardIcon />
                </Fab>
            </Tooltip>
        </Zoom>
    );
}

export default memo(ScrollToTopButton);
